// The Milky Way as 3D particles: stars, young-star and gas clouds, HII
// knots, the smooth light of the old disc and bulge, and dust, every one a
// particle placed in 3D (kpc, galaxy-local, z the disc normal).
//
// The technique follows the procedural galaxies of Gaia Sky (Gaia Sky itself
// is Sagrista et al. 2019, IEEE TVCG 25(1); the channels of stars, HII, gas,
// dust and bulge particles, each with a capped size, are described in the
// "Procedural galaxies" chapter of its documentation, since version 3.7.1)
// and Microsoft's Galaxy Explorer (stars on density-wave ellipses, generated
// once and moved on the GPU). Two changes for realism:
//
// - Every particle except a star is a 3D Gaussian, oblate along the disc
//   normal, projected to its screen ellipse each frame (the elliptical
//   weighted average splat of Zwicker et al. 2001, as Gaussian splatting
//   uses it). Seen face-on a dust cloud or gas cloud is round, edge-on it
//   is as thin as its layer, so the band, its dust lane and the bulge come
//   out of the same particles from every side.
// - Emission and absorption share one blend (premultiplied over: emitters
//   add light with no alpha, dust takes away a share of what is behind it),
//   drawn back to front, so dust dims only the light behind it. The disc is
//   cut into columns that three.js sorts by depth each frame, and inside a
//   column the particles are ordered by height, flipped by the side of the
//   disc the camera is on. Dust is clumpy and filamentary, as it is in the
//   radiative transfer images of simulated Milky Way-like galaxies, where it
//   follows the clumpy, cold gas (Kapoor et al. 2021, MNRAS 506, 5703).
//
// Structure is the measured one (MW_STRUCTURE). Colours are the approved
// OKLCh palette. Brightness is physical: a splat's surface brightness is
// its luminosity over its projected area, so it does not change with
// distance, and a star's is its flux, which does; both are capped near the
// camera, so the disc holds up from inside.
import * as THREE from 'three';

// The disc's thicknesses, scale length and warp are Andre's look, set with
// the tuner dock's Galaxy tab on 31 Oct 2026, with the thin disc, dust and
// young stars then brought about halfway back to the measured heights, as
// at his first values the disc seen edge-on was a loose swarm with no dust
// lane. The measured values: thinH 0.30 and thinL 2.6 (BHG16 5.1.1-5.1.2),
// thickH 0.90 and thickShare 0.12 (BHG16 5.1.3, f_rho 4 %, f_Sigma 12 %),
// dustH 0.13 (DS01, sech^2 134-188 pc), youngH 0.09, warpK 0.0274 (DS01
// table 2's rate).
export const MW_STRUCTURE = {
  thinH: 0.42, thinL: 4.1,
  thickH: 0.72, thickL: 2.0, thickShare: 0.32,
  dustH: 0.18,
  youngH: 0.12,
  barAngleDeg: 27,                         // BHG16 4.2
  warpK: 0.046, warpR: 9.0, warpMax: 5.0,  // starting at 9 kpc and capped: with the full warp the near side's dust lay across the bulge edge-on
  sunR: 8.2, sunZ: 0.025,                  // BHG16 3.2.4, 3.3
};

// The arms in the pattern frame, after the NASA/JPL-Caltech (R. Hurt)
// reconstruction: two major arms from the bar's ends, two minor arms, the
// Orion spur and two outer branches.
export function milkyWayArms(barAngle) {
  const T = (deg) => Math.tan(deg * Math.PI / 180);
  const at = (arm, r) => arm.theta0 + Math.log(r / arm.r0) / T(arm.pitch);
  const sc = { theta0: barAngle, r0: 4.1, pitch: 12.6, weight: 1.0, rEnd: 16.5, width: 1.0, dust: 1.0, seed: 1.3 };
  const pe = { theta0: barAngle + Math.PI, r0: 4.1, pitch: 13.8, weight: 0.92, rEnd: 15.8, width: 1.1, dust: 0.9, seed: 4.1 };
  const no = { theta0: barAngle + Math.PI * 0.5 + 0.55, r0: 4.7, pitch: 11.5, weight: 0.42, rEnd: 17.2, width: 0.85, dust: 0.7, seed: 2.2 };
  const sg = { theta0: barAngle + Math.PI * 1.5 + 0.35, r0: 4.4, pitch: 14.5, weight: 0.5, rEnd: 13.5, width: 0.8, dust: 0.75, seed: 6.6 };
  const orion = { theta0: at(pe, 6.6) + 1.05, r0: 6.6, pitch: 24, weight: 0.3, rEnd: 10.8, width: 0.6, dust: 0.5, seed: 8.8 };
  const scB = { theta0: at(sc, 9.2) - 0.04, r0: 9.2, pitch: 20, weight: 0.36, rEnd: 15.5, width: 0.75, dust: 0.55, seed: 3.7 };
  const peB = { theta0: at(pe, 10.6) + 0.03, r0: 10.6, pitch: 8.5, weight: 0.3, rEnd: 16.8, width: 0.7, dust: 0.5, seed: 5.3 };
  return [sc, pe, no, sg, orion, scB, peB].map((a) => ({ ...a, b: T(a.pitch) }));
}

// OKLCh to linear sRGB.
function oklch(L, C, hDeg) {
  const h = hDeg * Math.PI / 180;
  const a = C * Math.cos(h), b = C * Math.sin(h);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.2914855480 * b) ** 3;
  return [
    Math.max(0, 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    Math.max(0, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    Math.max(0, -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s),
  ];
}
// Mixes in OKLCh (L, C and hue along the short arc), then to linear sRGB.
const lch = (L, C, h) => ({ L, C, h });
function mixLch(a, b, t) {
  let dh = b.h - a.h;
  if (dh > 180) dh -= 360;
  if (dh < -180) dh += 360;
  return lch(a.L + (b.L - a.L) * t, a.C + (b.C - a.C) * t, a.h + dh * t);
}
const rgbOf = (c) => oklch(c.L, c.C, c.h);
// The palette, started from the approved face-on pass and set against the
// photographs' regions measured in OKLCh (M51 HST, M101, M31, NGC 891,
// NGC 4565: bulge core hue 55-76, bulge edge and inner disc 45-71 and
// 52-53 at chroma 0.03-0.045, arms 250-256, interarm warm 33-48, HII
// 19-22 at chroma 0.07-0.09, dust lanes brown 51-59). Chroma sits above
// the photographs' because the tone map takes some of it out, and the arm
// blue leans cyan because summed with the warm disc light it drifts toward
// violet.
export const GALAXY_PALETTE = {
  oldIn: lch(0.80, 0.060, 38), oldOut: lch(0.80, 0.016, 38), thick: lch(0.78, 0.028, 50),
  hazeWarm: lch(0.82, 0.024, 55), hazeBlue: lch(0.80, 0.045, 232),
  young: lch(0.84, 0.075, 232), cluster: lch(0.93, 0.055, 232), hii: lch(0.64, 0.200, 32),
  core: lch(0.88, 0.115, 64), dust: lch(0.355, 0.084, 73),
  starYoung: lch(0.86, 0.10, 236), starOld: lch(0.86, 0.085, 62), starBulge: lch(0.84, 0.10, 58),
};
const P = GALAXY_PALETTE;

// The rest of the look in one place, so the tuner dock's Galaxy tab
// (main.js) can set it: the drawn gains and exposure (live, read each
// frame), and the light each population carries, the dust and the shape
// (read by generate, so a change needs rebuild()). Light totals are in the
// splats' amount units; every population is normalised to its own, so
// particleCount changes the grain and not the brightness. The values are
// Andre's, set with that tab on 31 Oct 2026 (the dust colour in
// GALAXY_PALETTE too).
export const GALAXY_LOOK = {
  // Live.
  starGain: 4, cloudGain: 1, dustGain: 13.55, dustGlow: 0.18,
  exposure: 1.34, edgeOnExposure: 0.67, insideDim: 0.57,
  // Light.
  youngStarLight: 14, oldStarLight: 8, bulgeStarLight: 2.5,
  complexLight: 10, armLight: 65, discLight: 81, bulgeLight: 30.1, bulgeEnvLight: 30, hazeLight: 43, nucleusLight: 1.55,
  // Dust.
  laneDust: 1, smoothDust: 1, barDust: 1.47, spurDust: 1.8,
  // Shape.
  armWidth: 0.63, armWander: 0.33, hiiShare: 0.47, particleCount: 1.45,
};
const LOOK = GALAXY_LOOK;

// Seeded generator and value noise, so the galaxy is the same on every load.
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function hash2(x, y) {
  const h = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return h - Math.floor(h);
}
function vnoise(x, y) {
  const ix = Math.floor(x), iy = Math.floor(y);
  const fx = x - ix, fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
  const a = hash2(ix, iy), b = hash2(ix + 1, iy), c = hash2(ix, iy + 1), d = hash2(ix + 1, iy + 1);
  return (a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy) * 2 - 1;
}
function fbm(x, y, oct = 3) {
  let s = 0, a = 0.5;
  for (let i = 0; i < oct; i++) { s += a * vnoise(x, y); x = x * 2.03 + 17.1; y = y * 2.03 + 9.2; a *= 0.5; }
  return s;
}
const smooth = (e0, e1, x) => { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };

const KIND_STAR = 0, KIND_EMIT = 1, KIND_DUST = 2;

/**
 * Generate every particle. Returns flat arrays; positions in kpc in the
 * pattern frame.
 */
function generate({ isMobile, barAngle }) {
  const S = MW_STRUCTURE;
  const q = (isMobile ? 0.25 : 1) * LOOK.particleCount;
  const counts = {
    youngStars: Math.round(100_000 * q), oldStars: Math.round(90_000 * q), bulgeStars: Math.round(40_000 * q),
    complexes: Math.round(5200 * q), discGlow: Math.round(1200 * q), bulgeGlow: Math.round(1400 * q),
    hazeGlow: Math.round(1200 * q), dustSeeds: Math.round(5000 * q), dustSmooth: Math.round(1800 * q),
    armLight: Math.round(10000 * q), bulgeEnv: Math.round(700 * q),
  };
  const cap = counts.youngStars + counts.oldStars + counts.bulgeStars + counts.complexes * 12
    + counts.discGlow + counts.bulgeGlow + counts.hazeGlow + counts.dustSeeds * 36 + counts.dustSmooth + counts.armLight + counts.bulgeEnv + counts.complexes * 6 + 40000;
  const pos = new Float32Array(cap * 3);
  const col = new Uint8Array(cap * 4);
  const shp = new Float32Array(cap * 4);
  let n = 0;
  const rand = mulberry32(912026);
  const gauss = () => { let u = 0; while (u === 0) u = rand(); return Math.sqrt(-2 * Math.log(u)) * Math.cos(6.283185307 * rand()); };
  const sech2z = (h) => { const u = rand() * 0.998 + 0.001; return h * Math.atanh(2 * u - 1); }; // z for sech^2(z/h)/(2h)
  const corrug = (x, y) => 0.06 * fbm(x * 0.32 + 5, y * 0.32 + 5);
  const warp = (x, y) => {
    const R = Math.hypot(x, y);
    const wf = Math.min(Math.max(R - S.warpR, 0), S.warpMax);
    return S.warpK * wf * wf * (y * 0.94 + x * 0.34) / Math.max(R, 1e-3);
  };
  const push = (x, y, z, kind, sA, sC, amount, rgb) => {
    if (n >= cap) return;
    pos[n * 3] = x; pos[n * 3 + 1] = y; pos[n * 3 + 2] = z + warp(x, y);
    // Colour as a unit-peak byte triple; its scale rides in amount.
    const m = Math.max(rgb[0], rgb[1], rgb[2], 1e-6);
    col[n * 4] = Math.round(255 * rgb[0] / m); col[n * 4 + 1] = Math.round(255 * rgb[1] / m); col[n * 4 + 2] = Math.round(255 * rgb[2] / m);
    col[n * 4 + 3] = kind;
    shp[n * 4] = kind; shp[n * 4 + 1] = sA; shp[n * 4 + 2] = sC; shp[n * 4 + 3] = amount * m;
    n++;
  };

  const arms = milkyWayArms(barAngle);
  // Branches: short arm segments leaving the arms at a wider pitch, as M101's
  // arms branch and fork; seeded, so the same every load.
  {
    const base = arms.slice(0, 4);
    for (let k = 0; k < 9; k++) {
      const a = base[k % 4];
      const r0 = 5.5 + rand() * 7;
      if (r0 > a.rEnd - 2) continue;
      const ln = Math.log(r0 / a.r0);
      const th0 = a.theta0 + ln / a.b + (rand() - 0.5) * 0.08;
      const pitch = 20 + rand() * 14;
      const b = Math.tan(pitch * Math.PI / 180);
      arms.push({ theta0: th0, r0, pitch, b, weight: 0.16 + 0.18 * rand(), rEnd: Math.min(r0 + 2.5 + rand() * 4, 17), width: 0.5 + 0.3 * rand(), dust: 0.4 + 0.3 * rand(), seed: 11 + k * 2.7 });
    }
  }
  // Arm wander: arms drift off a perfect log spiral by up to ~0.8 kpc.
  const wander = (x, y) => [fbm(x * 0.11 + 3.1, y * 0.11 + 3.1) * 1.25 * LOOK.armWander, fbm(x * 0.11 + 9.7, y * 0.11 + 9.7) * 1.25 * LOOK.armWander];
  const armWidth = (a, r) => (0.26 + 0.034 * r) * a.width * LOOK.armWidth * (0.5 + 1.0 * smooth(-0.45, 0.45, vnoise(r * 0.55, a.seed * 3.3 + 2) + 0.4 * vnoise(r * 1.7, a.seed)));
  const alongB = (a, r) => 0.15 + 1.3 * smooth(-0.1, 0.5, vnoise(r * 0.95, a.seed * 5.3) + 0.5 * vnoise(r * 2.3, a.seed * 9.1));
  const armEnv = (a, r) => smooth(a.r0 * 0.88, a.r0 * 1.18, r) * (1 - smooth(a.rEnd * 0.72, a.rEnd, r));
  const youngDisc = (r) => Math.exp(-r / 6.5) * smooth(1.8, 4.0, r) * (1 - smooth(11.0, 15.8, r));
  // A point s kpc across arm a at radius r (positive toward the concave
  // side), with its local along-arm direction.
  // The arm's angle at radius r: a log spiral that opens out where it
  // leaves the bar (pitch near 40 degrees at its start, settling to its own
  // within about a kiloparsec), so the arms visibly wind out of the bar ends
  // instead of hugging the bulge as a ring.
  const OPEN = 0.25, OPEN_L = 0.35;
  const armPoint = (a, r, s) => {
    const ln = Math.log(r / a.r0);
    const lnc = Math.max(ln, 0);
    const bend = 0.13 * Math.sin(ln * 2.1 + a.seed) + 0.07 * Math.sin(ln * 4.7 + a.seed * 2.3);
    const th = a.theta0 + (ln - OPEN * (1 - Math.exp(-lnc / OPEN_L))) / a.b + bend;
    const dth = (1 - (OPEN / OPEN_L) * Math.exp(-lnc / OPEN_L)) / a.b; // d theta / d ln r
    const bEff = 1 / Math.max(dth, 1e-3);
    const sinPsi = bEff / Math.sqrt(1 + bEff * bEff);
    const thp = th + s / (r * sinPsi);
    let x = r * Math.cos(thp), y = r * Math.sin(thp);
    const w = wander(x, y);
    x -= w[0]; y -= w[1];
    // Tangent of the spiral: b e_r + e_theta.
    const tx = bEff * Math.cos(thp) - Math.sin(thp), ty = bEff * Math.sin(thp) + Math.cos(thp);
    const tl = Math.hypot(tx, ty);
    return [x, y, tx / tl, ty / tl];
  };
  const pickArm = (weightKey) => {
    let tot = 0;
    for (const a of arms) tot += a[weightKey];
    let u = rand() * tot;
    for (const a of arms) { u -= a[weightKey]; if (u <= 0) return a; }
    return arms[0];
  };
  // Young light across an arm: sharp on the concave side, trailing on the
  // convex side (areas 0.485 w and 1.31 w).
  const youngS = (w) => (rand() < 0.27 ? Math.abs(gauss()) * Math.sqrt(0.15) * w : -1.45 * w * Math.pow(-Math.log(1 - rand() * 0.999), 1 / 1.5));
  const colOld = (r) => rgbOf(mixLch(P.oldIn, P.oldOut, smooth(1.5, 9.0, r)));
  const cYoung = rgbOf(P.young), cCluster = rgbOf(P.cluster), cHii = rgbOf(P.hii), cCore = rgbOf(P.core);
  const cThick = rgbOf(P.thick), cDust = rgbOf(P.dust);
  const cHiiMix = rgbOf(mixLch(P.hii, P.cluster, 0.12));

  // ---- Star-forming complexes along the arms ----
  // Each a clump of young stars, gas and sometimes HII knots, a few tens to
  // a few hundred parsecs across, at its own height, stretched along the arm.
  const complexes = [];
  while (complexes.length < counts.complexes) {
    const a = pickArm('weight');
    const r = a.r0 * 0.88 + rand() * (a.rEnd - a.r0 * 0.88);
    const wgt = a.weight * armEnv(a, r) * alongB(a, r) * youngDisc(r) * 3.2;
    if (rand() > wgt) continue;
    const w = armWidth(a, r);
    // HII complexes string along the leading (concave) edge, next to the
    // dust lane, as in M51; the rest spread across the arm.
    const hii = rand() < LOOK.hiiShare;
    // HII complexes sit along and just downstream of the dust lane (the
    // lane is at +0.42 w on the concave side), scattered, not on a line.
    const s = hii ? (0.30 - 0.35 * Math.abs(gauss())) * w : youngS(w) * 0.8;
    const [x, y, tx, ty] = armPoint(a, r, s);
    const size = 0.03 + 0.32 * Math.pow(rand(), 2.6);
    const z = gauss() * 0.045 + corrug(x, y);
    complexes.push({ x, y, z, tx, ty, size, r, hii, w: 0.4 + rand() * 1.2 });
  }
  const totalYoungLight = LOOK.complexLight;
  // Spurs: a quarter of the complexes trail a short string of young light
  // and dust out across the gap at 35-55 degrees to the arm, downstream.
  for (const c of complexes) {
    if (rand() > 0.25) continue;
    const ang = -(0.6 + 0.35 * rand());
    const dx = c.tx * Math.cos(ang) - c.ty * Math.sin(ang), dy = c.tx * Math.sin(ang) + c.ty * Math.cos(ang);
    const len = 4 + (rand() * 8 | 0);
    for (let k = 1; k <= len; k++) {
      const d = k * (0.06 + 0.04 * rand());
      const x = c.x + dx * d + gauss() * 0.03, y = c.y + dy * d + gauss() * 0.03;
      push(x, y, c.z + gauss() * 0.03, KIND_EMIT, 0.04 + 0.05 * rand(), 0.03, totalYoungLight * 0.6 / counts.complexes / len, rand() < 0.5 ? cYoung : cCluster);
      if (k % 2 === 0) push(x + c.tx * 0.03, y + c.ty * 0.03, c.z + gauss() * 0.02, KIND_DUST, 0.08, 0.03, 0.0006 * LOOK.spurDust / LOOK.particleCount, cDust);
    }
  }
  for (const c of complexes) {
    const along = c.size * 1.6, across = c.size * 0.6, up = Math.min(c.size * 0.35, 0.07);
    // Young-star cloud light: a few soft oblate splats.
    const ng = 1 + Math.floor(rand() * 3);
    for (let k = 0; k < ng; k++) {
      const u = gauss() * along * 0.5, v = gauss() * across * 0.5;
      const x = c.x + c.tx * u - c.ty * v, y = c.y + c.ty * u + c.tx * v;
      push(x, y, c.z + gauss() * up * 0.5, KIND_EMIT, c.size * (0.5 + 0.4 * rand()), up * 0.8 + 0.02,
        totalYoungLight * c.w / counts.complexes / ng, rand() < 0.5 ? cYoung : cCluster);
    }
    if (c.hii) {
      // A complex is an irregular clump: one to a dozen knots of sizes from
      // a power law (many small, few large), brightness with their area.
      const nh = 1 + Math.floor(Math.pow(rand(), 2) * 12);
      for (let k = 0; k < nh; k++) {
        const u = gauss() * along * 0.8, v = gauss() * across * 0.9;
        const x = c.x + c.tx * u - c.ty * v, y = c.y + c.ty * u + c.tx * v;
        const sz = 0.010 + 0.07 * Math.pow(rand(), 3);
        push(x, y, c.z + gauss() * up * 0.5, KIND_EMIT, sz, sz * 0.85, 0.0004 + 0.35 * sz * sz * (0.5 + rand()), rand() < 0.8 ? cHii : cHiiMix);
      }
    }
  }

  // Each star population's total light is set (in the same units as the
  // splats' amounts), so the stars and the smooth light share it sensibly.
  const normalise = (from, total) => {
    let sum = 0;
    for (let i = from; i < n; i++) sum += shp[i * 4 + 3];
    const k = total / Math.max(sum, 1e-9);
    for (let i = from; i < n; i++) shp[i * 4 + 3] *= k;
  };
  // ---- Continuous arm light ----
  // Overlapping splats along every arm, sizes from a power law (many small,
  // few large), so the arms are continuous with clumps in them, not beads.
  const armLightTotal = LOOK.armLight;
  for (let i = 0; i < counts.armLight; i++) {
    let a, r;
    for (;;) {
      a = pickArm('weight');
      r = a.r0 * 0.88 + rand() * (a.rEnd - a.r0 * 0.88);
      if (rand() < a.weight * armEnv(a, r) * alongB(a, r) * youngDisc(r) * 3.2) break;
    }
    const w = armWidth(a, r);
    const [x, y] = armPoint(a, r, youngS(w));
    const sA = 0.03 + 0.42 * Math.pow(rand(), 3);
    const sC = Math.min(0.5 * sA, 0.08) + 0.02;
    push(x, y, sech2z(S.youngH * 0.8) + corrug(x, y), KIND_EMIT, sA, sC, armLightTotal / counts.armLight * (0.5 + sA * 4),
      rand() < 0.5 ? cYoung : cCluster);
  }
  // ---- Young stars: in the complexes, and a looser arm population ----
  const youngFrom = n;
  for (let i = 0; i < counts.youngStars; i++) {
    let x, y, z;
    if (rand() < 0.6) {
      const c = complexes[(rand() * complexes.length) | 0];
      const u = gauss() * c.size * 0.8, v = gauss() * c.size * 0.35;
      x = c.x + c.tx * u - c.ty * v; y = c.y + c.ty * u + c.tx * v;
      z = c.z + gauss() * Math.min(c.size * 0.3, 0.06);
    } else {
      let a, r;
      for (;;) {
        a = pickArm('weight');
        r = a.r0 * 0.88 + rand() * (a.rEnd - a.r0 * 0.88);
        if (rand() < a.weight * armEnv(a, r) * alongB(a, r) * youngDisc(r) * 3.2) break;
      }
      [x, y] = armPoint(a, r, youngS(armWidth(a, r)));
      z = sech2z(S.youngH) + corrug(x, y);
    }
    // A Salpeter-like luminosity function: few bright, many faint.
    const L = Math.min(150, Math.pow(1 - rand() * 0.99999, -0.75));
    push(x, y, z, KIND_STAR, 0, 0, L, rgbOf(mixLch(P.starYoung, lch(0.96, 0.02, 240), rand() * rand())));
  }

  normalise(youngFrom, LOOK.youngStarLight);
  // ---- Old disc stars: exponential, swept inside the bar, swelling on arms ----
  const sampleDiscR = () => {
    for (;;) {
      // Gamma(2) in R/L is the area-weighted exponential disc.
      const R = -S.thinL * Math.log(rand() * rand() + 1e-9);
      if (R > 18.5) continue;
      const sweep = 1 - 0.6 * Math.exp(-((R / 3.4) ** 2));
      const outer = 1 - smooth(12.5, 18.8, R);
      if (rand() < sweep * outer) return R;
    }
  };
  const oldFrom = n;
  for (let i = 0; i < counts.oldStars; i++) {
    let x, y;
    const R = sampleDiscR();
    const aOld = arms[rand() < 0.5 ? 0 : 1];
    // On an arm only where the arm is: a radius outside it, clamped onto
    // its end, piled particles on the circle through that end.
    if (rand() < 0.3 && R > aOld.r0 && R < aOld.rEnd) {
      [x, y] = armPoint(aOld, R, gauss() * armWidth(aOld, R) * 1.6);
    } else {
      const th = rand() * 6.283185307;
      x = R * Math.cos(th); y = R * Math.sin(th);
    }
    const thick = rand() < S.thickShare;
    const z = sech2z(thick ? S.thickH : S.thinH);
    const L = Math.min(30, Math.pow(1 - rand() * 0.99999, -0.75));
    push(x, y, z, KIND_STAR, 0, 0, L, rgbOf(mixLch(P.starOld, lch(0.92, 0.03, 80), rand() * rand())));
  }

  // ---- Bulge and bar: boxy/peanut with its X, the long bar, the nucleus ----
  const cb = Math.cos(barAngle), sb = Math.sin(barAngle);
  const bulgePoint = () => {
    for (;;) {
      const bx = (rand() * 2 - 1) * 5.0, by = (rand() * 2 - 1) * 1.8, bz = (rand() * 2 - 1) * 1.8;
      const rp2 = (bx / 0.7) ** 2 + (by / 0.44) ** 2, z2 = (bz / 0.30) ** 2;
      const box = Math.exp(-Math.sqrt(Math.sqrt(rp2 * rp2 + z2 * z2)));
      const ax = Math.abs(bx);
      const xArm = Math.exp(-(((Math.abs(bz) - 0.5 * ax) / 0.16) ** 2)) * Math.exp(-((by / 0.55) ** 2)) * Math.exp(-((ax / 1.9) ** 3)) * Math.min(1, ax / 0.9);
      const bar = 0.12 * Math.exp(-((ax / 4.4) ** 4) - (by / 0.7) ** 2) * Math.exp(-Math.abs(bz) / 0.2);
      const outer = 0.25 * Math.exp(-Math.sqrt(bx * bx / 1.6 + by * by + bz * bz / 0.8) / 0.55);
      // A soft spheroidal envelope round the box: the light people see
      // reaches about +-1.2 kpc in height and 2 kpc along the bar.
      const env = 0.55 * Math.exp(-Math.sqrt(bx * bx / 1.0 + by * by / 0.8 + bz * bz / 0.72) / 0.42);
      if (rand() < Math.min(1, 0.95 * box + 0.15 * xArm + bar + outer + env)) {
        return [bx * cb - by * sb, bx * sb + by * cb, bz];
      }
    }
  };
  normalise(oldFrom, LOOK.oldStarLight);
  const bulgeFrom = n;
  for (let i = 0; i < counts.bulgeStars; i++) {
    const [x, y, z] = bulgePoint();
    const L = Math.min(20, Math.pow(1 - rand() * 0.99999, -0.75));
    push(x, y, z, KIND_STAR, 0, 0, L, rgbOf(mixLch(P.starBulge, lch(0.90, 0.04, 75), rand() * rand())));
  }

  normalise(bulgeFrom, LOOK.bulgeStarLight);
  // ---- Smooth light: the unresolved old disc, the bulge, the haze ----
  // Broad oblate splats, as thin as their layer, so the light is continuous
  // and the edge-on band has the measured thickness.
  const discLight = LOOK.discLight;
  for (let i = 0; i < counts.discGlow; i++) {
    const R = sampleDiscR();
    const th = rand() * 6.283185307;
    const x = R * Math.cos(th), y = R * Math.sin(th);
    const thick = rand() < 0.15;
    const sA = 0.35 + 0.05 * R + 0.25 * rand();
    push(x, y, sech2z(thick ? 0.5 : 0.10), KIND_EMIT, sA, thick ? S.thickH : 0.22, discLight / counts.discGlow * (thick ? 0.9 : 1), thick ? cThick : colOld(R));
  }
  // The bulge's smooth light: round splats (as tall as wide) filling the
  // bulb, warm cream at the core fading to the old disc's colour outward.
  const bulgeLight = LOOK.bulgeLight;
  for (let i = 0; i < counts.bulgeGlow; i++) {
    const [x, y, z] = bulgePoint();
    const rr = Math.hypot(x, y, z);
    const sA = 0.22 + 0.35 * rand();
    push(x, y, z, KIND_EMIT, sA, sA * 0.92, bulgeLight / counts.bulgeGlow, rgbOf(mixLch(P.core, P.oldIn, smooth(0.2, 1.6, rr))));
  }
  // The bulb's soft envelope: large round splats from the spheroid alone,
  // so the light fills about +-1.2 kpc in height and 2 kpc along the bar.
  const envLight = LOOK.bulgeEnvLight;
  for (let i = 0; i < counts.bulgeEnv; i++) {
    let x, y, z;
    for (;;) {
      x = (rand() * 2 - 1) * 3; y = (rand() * 2 - 1) * 3; z = (rand() * 2 - 1) * 2.4;
      if (rand() < Math.exp(-Math.sqrt(x * x / 1.0 + y * y / 0.8 + z * z / 0.72) / 0.42)) break;
    }
    const xb = x * cb - y * sb, yb = x * sb + y * cb;
    const sA = 0.45 + 0.45 * rand();
    push(xb, yb, z, KIND_EMIT, sA, sA, envLight / counts.bulgeEnv, rgbOf(mixLch(P.core, P.oldIn, smooth(0.3, 2.0, Math.hypot(x, y, z)))));
  }
  // Nucleus: a small bright core.
  push(0, 0, 0, KIND_EMIT, 0.04, 0.035, 0.2 * LOOK.nucleusLight, cCore);
  push(0, 0, 0, KIND_EMIT, 0.14, 0.12, 0.35 * LOOK.nucleusLight, cCore);
  const hazeLight = LOOK.hazeLight;
  for (let i = 0; i < counts.hazeGlow; i++) {
    let R;
    for (;;) { R = 1.5 + rand() * 17; if (rand() < Math.exp(-R / 8) * smooth(1.5, 5, R) * (1 - smooth(12.5, 18.8, R)) * R / 6) break; }
    let x, y;
    const aH = pickArm('weight');
    if (rand() < 0.6 && R > aH.r0 && R < aH.rEnd) {
      [x, y] = armPoint(aH, R, -Math.abs(gauss()) * armWidth(aH, R) * 1.2);
    } else {
      const th = rand() * 6.283185307;
      x = R * Math.cos(th); y = R * Math.sin(th);
    }
    const out = smooth(4.5, 10, R);
    push(x, y, sech2z(0.15), KIND_EMIT, 0.35 + 0.3 * rand(), 0.22, hazeLight / counts.hazeGlow,
      out > rand() ? rgbOf(P.hazeBlue) : rgbOf(P.hazeWarm));
  }

  // ---- Dust ----
  // Filaments: chains of small oblate clouds along the arms' concave edges,
  // broken into segments, with feathers crossing the gaps and the odd
  // tendril rising out of the layer.
  const chain = (x, y, z, dx, dy, dz, steps, step, sA, sC, mass) => {
    let vx = dx, vy = dy, vz = dz;
    for (let k = 0; k < steps; k++) {
      push(x, y, z, KIND_DUST, sA * (0.6 + 0.8 * rand()), sC, mass * (0.4 + 1.2 * rand()), cDust);
      // Drift: the direction turns a little at each step.
      vx += gauss() * 0.25; vy += gauss() * 0.25; vz += gauss() * 0.015;
      const l = Math.hypot(vx, vy, vz) || 1;
      vx /= l; vy /= l; vz /= l;
      x += vx * step; y += vy * step; z += vz * step * 0.5;
      z *= 0.92; // pulled back toward the layer
    }
  };
  for (let i = 0; i < counts.dustSeeds; i++) {
    const a = pickArm('dust');
    const r = a.r0 * 0.88 + rand() * (a.rEnd - a.r0 * 0.88);
    const env = armEnv(a, r) * smooth(2.0, 4.0, r) * (1 - smooth(14, 17, r));
    const brk = smooth(-0.10, 0.45, vnoise(r * 1.3, a.seed * 6.1 + 4) + 0.55 * vnoise(r * 3.7, a.seed));
    if (rand() > env * (0.15 + 0.85 * brk)) { i--; continue; }
    const w = armWidth(a, r);
    const s = 0.42 * w + gauss() * 0.15 * w;
    const [x, y, tx, ty] = armPoint(a, r, s);
    const z = sech2z(S.dustH * 0.7) + corrug(x, y);
    const fray = smooth(a.rEnd * 0.45, a.rEnd * 0.9, r);
    const mass = 0.004 * LOOK.laneDust / LOOK.particleCount * a.dust * (1 - 0.6 * fray);
    const u = rand();
    if (u < 0.72) {
      // Lane: overlapping clouds (step under a cloud's width), continuous.
      chain(x, y, z, tx, ty, 0, 10 + (rand() * 20 | 0), 0.035 + 0.025 * rand(), 0.10, 0.06, mass);
    } else if (u < 0.96) {
      // Feather or spur: leaves the lane toward the convex side at 40 to
      // 60 degrees and crosses the gap, as in M51.
      const ang = -(0.7 + 0.35 * rand());
      const c = Math.cos(ang), sn = Math.sin(ang);
      chain(x, y, z, tx * c - ty * sn, tx * sn + ty * c, 0, 14 + (rand() * 22 | 0), 0.045, 0.08, 0.035, mass * 0.7);
    } else if (r > 5) {
      // Tendril: rises out of the layer (outside the bulge).
      chain(x, y, z, tx * 0.3, ty * 0.3, rand() < 0.5 ? 0.9 : -0.9, 6 + (rand() * 10 | 0), 0.06, 0.05, 0.04, mass * 0.5);
    }
  }
  // Bar lanes on the leading sides, and the smooth dust disc (DS01: 2.3 kpc,
  // a hole inside about 4 kpc, face-on depth about 0.2 at the Sun).
  // Bar lanes: ragged, broken, curving at the ends, many small clouds
  // wandering about the lane's line rather than one row of beads.
  for (let side = -1; side <= 1; side += 2) {
    const nb = isMobile ? 140 : 520;
    for (let k = 0; k < nb; k++) {
      const t = rand();
      const bx = side * (0.5 + t * 3.6);
      if (vnoise(bx * 1.3, side * 7) < -0.3) continue; // gaps
      // Wider scatter, softer clouds, and fading in toward the centre, so the
      // lanes follow the bar's leading edges and dissolve into its light.
      const by = -side * (0.30 + 0.16 * (Math.abs(bx) / 4) ** 2) + gauss() * 0.07 + 0.08 * vnoise(bx * 2.5, side * 3);
      const sA = 0.04 + 0.06 * rand();
      const fade = smooth(0.5, 1.6, Math.abs(bx)) * (1 - smooth(3.4, 4.1, Math.abs(bx)));
      push(bx * cb - by * sb, bx * sb + by * cb, gauss() * 0.02, KIND_DUST, sA, 0.025, 0.0006 * LOOK.barDust * fade * (0.4 + rand()), cDust);
    }
  }
  for (let i = 0; i < counts.dustSmooth; i++) {
    let R;
    for (;;) { R = 2 + rand() * 16; if (rand() < Math.exp(-(R - 4) / 2.3) * smooth(2.5, 5, R) * (1 - smooth(14, 18, R)) * R / 4) break; }
    const th = rand() * 6.283185307;
    push(R * Math.cos(th), R * Math.sin(th), sech2z(S.dustH), KIND_DUST, 0.3 + 0.3 * rand(), S.dustH * 0.8, 0.0045 * LOOK.smoothDust * (3000 / counts.dustSmooth), cDust);
  }
  return { pos, col, shp, n };
}

// Column chunks: particles grouped into columns, ordered by height inside
// each, so drawing columns far to near and each one bottom to top (camera
// above) or top to bottom (camera below) is back to front. With halves, a
// column is split at the midplane into two objects whose sort points sit
// zHalf below and above it.
const CELL_KPC = 2.5;

function buildChunks(g, sel, { cell = CELL_KPC, halves = false, zHalf = 0.3, dustLast = false } = {}) {
  const { pos, col, shp } = g;
  const grid = Math.round(40 / cell);
  const n = sel.length;
  const ZB = 64;
  const NH = halves ? 2 : 1;
  // With dustLast, a column holds its emitters (by height) then its dust (by
  // height), so its dust dims all of its thin layer's light: edge-on, height
  // says nothing about depth inside a column, and drawn by height the near
  // columns' midplane light covered their own dust.
  const ND = dustLast ? 2 : 1;
  const zbin = (z) => Math.min(ZB - 1, Math.max(0, Math.floor((Math.asinh(z / 0.15) / Math.asinh(4 / 0.15) * 0.5 + 0.5) * ZB)));
  const key = new Uint32Array(n);
  const cnt = new Uint32Array(grid * grid * NH * ND * ZB + 1);
  for (let q = 0; q < n; q++) {
    const i = sel[q];
    const cx = Math.min(grid - 1, Math.max(0, Math.floor(pos[i * 3] / cell + grid / 2)));
    const cy = Math.min(grid - 1, Math.max(0, Math.floor(pos[i * 3 + 1] / cell + grid / 2)));
    const z = pos[i * 3 + 2];
    const h = halves && z >= 0 ? 1 : 0;
    const dd = dustLast && shp[i * 4] > 1.5 ? 1 : 0;
    const k = ((((cy * grid + cx) * NH) + h) * ND + dd) * ZB + zbin(z);
    key[q] = k;
    cnt[k + 1]++;
  }
  for (let k = 1; k < cnt.length; k++) cnt[k] += cnt[k - 1];
  const start = cnt.slice();
  const order = new Uint32Array(n);
  for (let q = 0; q < n; q++) order[start[key[q]]++] = q;
  const P3 = new Float32Array(n * 3), C4 = new Uint8Array(n * 4), S4 = new Float32Array(n * 4);
  // Positions are stored relative to their column's sort point, and each
  // column object sits at that point, which is what three.js sorts by.
  const centre = (k) => {
    const ch = Math.floor(k / (ZB * ND));
    const c = Math.floor(ch / NH), h = ch % NH;
    return [((c % grid) - grid / 2 + 0.5) * cell, (Math.floor(c / grid) - grid / 2 + 0.5) * cell, halves ? (h ? zHalf : -zHalf) : 0];
  };
  for (let j = 0; j < n; j++) {
    const q = order[j];
    const i = sel[q];
    const [ccx, ccy, ccz] = centre(key[q]);
    P3[j * 3] = pos[i * 3] - ccx; P3[j * 3 + 1] = pos[i * 3 + 1] - ccy; P3[j * 3 + 2] = pos[i * 3 + 2] - ccz;
    for (let c = 0; c < 4; c++) { C4[j * 4 + c] = col[i * 4 + c]; S4[j * 4 + c] = shp[i * 4 + c]; }
  }
  const chunks = [];
  for (let ch = 0; ch < grid * grid * NH; ch++) {
    const s0 = cnt[ch * ZB * ND], e0 = cnt[(ch + 1) * ZB * ND];
    if (e0 > s0) {
      const [cx, cy, cz] = centre(ch * ZB * ND);
      const emit = dustLast ? cnt[ch * ZB * ND + ZB] - s0 : e0 - s0;
      chunks.push({ start: s0, count: e0 - s0, emit, cx, cy, cz });
    }
  }
  return { P3, C4, S4, n, chunks, cell };
}

/**
 * Every particle, grouped into the columns it is drawn by: typed arrays and
 * plain lists only, so a worker can make it and hand it over without a copy.
 */
export function galaxyData({ isMobile, barAngle }) {
  const g = generate({ isMobile, barAngle });
  // The bulge's broad round clouds go to the quads, in half-columns either
  // side of the plane; everything in the disc stays in the point columns,
  // where each column's dust is drawn over its own light (edge-on, the
  // disc's light has to sit with its dust for the lane to read).
  const big = [], small = [];
  for (let i = 0; i < g.n; i++) {
    const k = g.shp[i * 4], sA = g.shp[i * 4 + 1], sC = g.shp[i * 4 + 2];
    (k > 0.5 && k < 1.5 && sA >= 0.2 && sC >= 0.75 * sA ? big : small).push(i);
  }
  const B = buildChunks(g, small, { cell: CELL_KPC, dustLast: true });
  const BQ = buildChunks(g, big, { cell: 5, halves: true, zHalf: 0.3 });
  // Reversed copies, for a camera below the disc.
  const revQ = (src, k) => {
    const out = new src.constructor(src.length);
    for (let j = 0; j < BQ.n; j++) for (let c = 0; c < k; c++) out[(BQ.n - 1 - j) * k + c] = src[j * k + c];
    return out;
  };
  const RP3 = revQ(BQ.P3, 3), RC4 = revQ(BQ.C4, 4), RS4 = revQ(BQ.S4, 4);
  // For a camera below: each column's emitters top to bottom, then its dust.
  const revArr = new Uint32Array(B.n);
  for (const c of B.chunks) {
    let o = c.start;
    for (let j = c.start + c.emit - 1; j >= c.start; j--) revArr[o++] = j;
    for (let j = c.start + c.count - 1; j >= c.start + c.emit; j--) revArr[o++] = j;
  }
  return { B, BQ, RP3, RC4, RS4, revArr };
}

// Stars and small clouds are points (a square sprite is cheap when small);
// the broad flattened glow clouds are oriented quads.
const POINT_VERT = /* glsl */`
  attribute vec4 shape;      // kind, sigma in plane (kpc), sigma across (kpc), amount
  uniform float uKpc;
  uniform float uPxPerRad;
  uniform float uPR;
  uniform float uMaxPx;
  uniform float uGainStar;
  uniform float uGainEmit;
  uniform float uGainDust;
  uniform float uExposure;
  varying vec4 vCol;         // emission (rgb, premultiplied); a: dust depth
  varying vec4 vEll;         // minor-axis screen direction (xy), sigma minor, sigma major (px)
  varying float vSize;
  varying float vKind;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    float dk = max(length(mv.xyz) / uKpc, 1e-4);
    float kind = shape.x;
    vKind = kind;
    vec3 rgb = color.rgb;
    if (kind < 0.5) {
      // A star: flux falls as 1/d^2, softened inside 30 pc and capped, a
      // Gaussian (sigma 0.65 px per pixel ratio) wide enough to move
      // smoothly between pixels; brighter stars spread wider, light kept.
      float sig0 = 0.65 * uPR;
      float sigRad = sig0 / uPxPerRad;
      float flux = shape.w / (dk * dk + 0.0009);
      float peak0 = flux / (6.2832 * sigRad * sigRad) * uGainStar;
      float grow = clamp(1.0 + 0.25 * log2(1.0 + peak0), 1.0, 2.0);
      float sig = sig0 * grow;
      float peak = peak0 / (grow * grow);
      vCol = vec4(rgb * min(peak, 2.0) * uExposure, 0.0);
      vEll = vec4(1.0, 0.0, sig, sig);
      vSize = 5.0 * sig;
      gl_PointSize = peak * uExposure < 0.003 || mv.z > 0.0 ? 0.0 : vSize;
      return;
    }
    // A small cloud: the oblate Gaussian's projected ellipse (see the quads).
    vec3 nrm = normalize(normalMatrix * vec3(0.0, 0.0, 1.0));
    vec3 v = normalize(mv.xyz);
    float ca = dot(nrm, v);
    float sA = shape.y, sC = shape.z;
    float sM = sqrt(sA * sA * ca * ca + sC * sC * (1.0 - ca * ca));
    vec2 e = nrm.xy - ca * v.xy;
    e = dot(e, e) > 1e-8 ? normalize(e) : vec2(1.0, 0.0);
    float pxA = sA / dk * uPxPerRad;
    float pxM = sM / dk * uPxPerRad;
    float a = max(pxA, 0.7 * uPR), m = max(pxM, 0.7 * uPR);
    float keep = (pxA * pxM) / (a * m);
    float sigma = shape.w / (6.2832 * sA * sM) * keep;
    float want = 5.0 * a;
    float near = 1.0 - smoothstep(0.55 * uMaxPx, uMaxPx, want);
    vSize = min(want, uMaxPx);
    vEll = vec4(e, m, a);
    if (kind < 1.5) {
      vCol = vec4(rgb * sigma * uGainEmit * near * uExposure, 0.0);
      gl_PointSize = (sigma * uGainEmit * near * uExposure < 1e-4 || mv.z > 0.0) ? 0.0 : vSize;
    } else {
      vCol = vec4(rgb, sigma * uGainDust * near);
      gl_PointSize = (vCol.a < 1e-3 || mv.z > 0.0) ? 0.0 : vSize;
    }
  }
`;

const POINT_FRAG = /* glsl */`
  uniform float uOpacity;
  uniform vec3 uDustTint;
  varying vec4 vCol;
  varying vec4 vEll;
  varying float vSize;
  varying float vKind;
  void main() {
    vec2 p = (gl_PointCoord - 0.5) * vSize;
    p.y = -p.y;
    float u = dot(p, vEll.xy);
    float w = dot(p, vec2(-vEll.y, vEll.x));
    float g = exp(-0.5 * (u * u / (vEll.z * vEll.z) + w * w / (vEll.w * vEll.w)));
    if (vKind < 1.5) {
      gl_FragColor = vec4(vCol.rgb * g * uOpacity, 0.0);
    } else {
      float a = (1.0 - exp(-vCol.a * g)) * uOpacity;
      gl_FragColor = vec4(uDustTint * a, a);
    }
  }
`;

// Each particle is an instanced quad: a star a small round one, a cloud an
// ellipse oriented on screen to its projected shape, so a cloud seen
// edge-on covers a thin strip instead of a square sized by its long axis.
const VERT = /* glsl */`
  attribute vec2 corner;      // quad corner, -1..1
  attribute vec3 iPos;        // kpc, relative to the column's centre
  attribute vec4 iCol;        // unit colour, kind
  attribute vec4 iShape;      // kind, sigma in plane (kpc), sigma across (kpc), amount
  uniform float uKpc;         // scene units per kpc
  uniform float uPxPerRad;    // target pixels per radian
  uniform vec2 uViewport;     // target size, pixels
  uniform float uPR;
  uniform float uMaxPx;
  uniform float uGainStar;
  uniform float uGainEmit;
  uniform float uGainDust;
  uniform float uExposure;
  varying vec3 vCol;          // premultiplied emission, or nothing for dust
  varying float vTau;         // dust depth at the centre
  varying vec2 vQ;            // position in the quad, in sigmas
  varying float vKind;
  const float EXTENT = 2.5;   // the quad reaches 2.5 sigma
  void main() {
    vec4 mv = modelViewMatrix * vec4(iPos, 1.0);
    vec4 clip = projectionMatrix * mv;
    float dk = max(length(mv.xyz) / uKpc, 1e-4);   // distance, kpc
    float kind = iShape.x;
    vKind = kind;
    vec3 rgb = iCol.rgb;
    vec2 e = vec2(1.0, 0.0);
    float a, m;
    bool show = mv.z < 0.0;
    vTau = 0.0;
    if (kind < 0.5) {
      // A star: flux falls as 1/d^2, softened inside 30 pc and capped,
      // drawn as a Gaussian (sigma 0.65 px per pixel ratio) wide enough to
      // move smoothly between pixels; brighter stars spread wider with
      // their light kept.
      float sig0 = 0.65 * uPR;
      float sigRad = sig0 / uPxPerRad;
      float flux = iShape.w / (dk * dk + 0.0009);
      float peak0 = flux / (6.2832 * sigRad * sigRad) * uGainStar;
      float grow = clamp(1.0 + 0.25 * log2(1.0 + peak0), 1.0, 2.0);
      a = m = sig0 * grow;
      float peak = peak0 / (grow * grow);
      vCol = rgb * min(peak, 2.0) * uExposure;
      show = show && peak * uExposure >= 0.003;
    } else {
      // A cloud: an oblate 3D Gaussian (sigma A in the disc plane, C
      // across) projected along the view ray. Across the projected normal's
      // perpendicular its sigma is A; along it, sqrt(A^2 cos^2 + C^2 sin^2)
      // (cos the angle between the ray and the normal).
      vec3 nrm = normalize(normalMatrix * vec3(0.0, 0.0, 1.0));
      vec3 v = normalize(mv.xyz);
      float ca = dot(nrm, v);
      float sA = iShape.y, sC = iShape.z;
      float sM = sqrt(sA * sA * ca * ca + sC * sC * (1.0 - ca * ca));
      vec2 ee = nrm.xy - ca * v.xy;
      e = dot(ee, ee) > 1e-8 ? normalize(ee) : vec2(1.0, 0.0);
      float pxA = sA / dk * uPxPerRad;
      float pxM = sM / dk * uPxPerRad;
      // Under 0.7 px a cloud widens and its peak drops, so the light it
      // carries is kept and it never shimmers.
      a = max(pxA, 0.7 * uPR);
      m = max(pxM, 0.7 * uPR);
      float keep = (pxA * pxM) / (a * m);
      // Surface density: amount over the projected area (kpc^-2), so the
      // surface brightness is the same at any distance.
      float sigma = iShape.w / (6.2832 * sA * sM) * keep;
      // Near the camera, clouds grow; past the size cap they fade out.
      float near = 1.0 - smoothstep(0.55 * uMaxPx, uMaxPx, 2.0 * EXTENT * a);
      if (kind < 1.5) {
        vCol = rgb * sigma * uGainEmit * near * uExposure;
        show = show && sigma * uGainEmit * near * uExposure >= 1e-4;
      } else {
        vCol = vec3(0.0);
        vTau = sigma * uGainDust * near;
        show = show && vTau >= 1e-3;
      }
    }
    vQ = corner * EXTENT;
    vec2 off = e * (vQ.x * m) + vec2(-e.y, e.x) * (vQ.y * a);
    clip.xy += off * 2.0 / uViewport * clip.w;
    gl_Position = show ? clip : vec4(2.0, 2.0, 2.0, 1.0);
  }
`;

const FRAG = /* glsl */`
  uniform float uOpacity;
  uniform vec3 uDustTint;
  varying vec3 vCol;
  varying float vTau;
  varying vec2 vQ;
  varying float vKind;
  void main() {
    float g = exp(-0.5 * dot(vQ, vQ));
    if (vKind < 1.5) {
      gl_FragColor = vec4(vCol * g * uOpacity, 0.0);
    } else {
      // Dust takes 1 - e^-tau of the light behind it and scatters back a
      // little warm light, so lanes read brown rather than black.
      float al = (1.0 - exp(-vTau * g)) * uOpacity;
      gl_FragColor = vec4(uDustTint * al, al);
    }
  }
`;

/**
 * The particle galaxy. Returns { group (pattern frame, scaled to kpc),
 * material, chunks, update(camera, renderer), dispose }.
 */
export function createParticleGalaxy({ renderer, unitsPerKpc, isMobile, barAngle }) {
  const uniforms = {
      uKpc: { value: unitsPerKpc },
      uPxPerRad: { value: 1000 },
      uPR: { value: renderer.getPixelRatio() },
      uMaxPx: { value: isMobile ? 500 : 900 },
      uViewport: { value: new THREE.Vector2(1, 1) },
      uGainStar: { value: LOOK.starGain },
      uGainEmit: { value: LOOK.cloudGain },
      uGainDust: { value: LOOK.dustGain },
      uExposure: { value: 1.0 },
      uOpacity: { value: 0 },
      uDustTint: { value: new THREE.Vector3(...rgbOf(P.dust)).multiplyScalar(LOOK.dustGlow) },
  };
  const blend = {
    transparent: true,
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
    blendSrcAlpha: THREE.ZeroFactor,
    blendDstAlpha: THREE.OneFactor,
    depthWrite: false,
    depthTest: true,
  };
  const material = new THREE.ShaderMaterial({ uniforms, vertexShader: VERT, fragmentShader: FRAG, ...blend });
  const pointMaterial = new THREE.ShaderMaterial({ uniforms, vertexShader: POINT_VERT, fragmentShader: POINT_FRAG, vertexColors: true, ...blend });
  // The particles live in their own scene and are drawn into their own
  // target (no multisampling, at CSS resolution, as Gaia Sky and Galaxy
  // Explorer draw their particle layers), then laid behind the scene. The
  // inner group copies the galaxy object's transform each frame.
  const pScene = new THREE.Scene();
  const holder = new THREE.Group();
  holder.matrixAutoUpdate = false;
  pScene.add(holder);
  const group = new THREE.Group();
  group.name = 'milkyWayParticles';
  group.scale.setScalar(unitsPerKpc);
  holder.add(group);
  const cornerAttr = new THREE.BufferAttribute(new Float32Array([-1, -1, 1, -1, 1, 1, -1, 1]), 2);
  const quadIndex = new THREE.BufferAttribute(new Uint16Array([0, 1, 2, 0, 2, 3]), 1);
  const mkGeom = (P3, C4, S4, start, count) => {
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('corner', cornerAttr);
    geo.setIndex(quadIndex);
    geo.setAttribute('iPos', new THREE.InstancedBufferAttribute(P3.subarray(start * 3, (start + count) * 3), 3));
    geo.setAttribute('iCol', new THREE.InstancedBufferAttribute(C4.subarray(start * 4, (start + count) * 4), 4, true));
    geo.setAttribute('iShape', new THREE.InstancedBufferAttribute(S4.subarray(start * 4, (start + count) * 4), 4));
    geo.instanceCount = count;
    return geo;
  };
  const mkPoints = (posAttr, colAttr, shpAttr, revIndex, indexed) => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', posAttr);
    geo.setAttribute('color', colAttr);
    geo.setAttribute('shape', shpAttr);
    if (indexed) geo.setIndex(revIndex);
    return geo;
  };
  // The particles as objects, from galaxyData(). rebuild() makes them again
  // after a change to GALAXY_LOOK, GALAXY_PALETTE or MW_STRUCTURE (the tuner
  // dock's Galaxy tab); the same seed gives the same galaxy.
  const chunks = [];
  let count = 0;
  let genMs = 0;
  let built = false;
  const build = ({ B, BQ, RP3, RC4, RS4, revArr }) => {
    const posAttr = new THREE.BufferAttribute(B.P3, 3);
    const colAttr = new THREE.BufferAttribute(B.C4, 4, true);
    const shpAttr = new THREE.BufferAttribute(B.S4, 4);
    const revIndex = new THREE.BufferAttribute(revArr, 1);
    for (const c of B.chunks) {
      const up = mkPoints(posAttr, colAttr, shpAttr, revIndex, false);
      up.setDrawRange(c.start, c.count);
      const down = mkPoints(posAttr, colAttr, shpAttr, revIndex, true);
      down.setDrawRange(c.start, c.count);
      const sphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), B.cell * 0.75 + 4.5);
      up.boundingSphere = sphere;
      down.boundingSphere = sphere;
      const pts = new THREE.Points(up, pointMaterial);
      pts.name = 'milkyWayChunk';
      pts.position.set(c.cx, c.cy, c.cz);
      pts.userData.up = up;
      pts.userData.down = down;
      pts.renderOrder = -900;
      group.add(pts);
      chunks.push(pts);
    }
    // The glow quads: each coarse column in two halves, below and above the
    // plane, whose sort points put the far half before the column's dust and
    // the near half after it.
    for (const c of BQ.chunks) {
      const up = mkGeom(BQ.P3, BQ.C4, BQ.S4, c.start, c.count);
      const down = mkGeom(RP3, RC4, RS4, BQ.n - c.start - c.count, c.count);
      const sphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), BQ.cell * 0.75 + 5.5);
      up.boundingSphere = sphere;
      down.boundingSphere = sphere;
      const mesh = new THREE.Mesh(up, material);
      mesh.name = 'milkyWayChunk';
      mesh.position.set(c.cx, c.cy, c.cz);
      mesh.userData.up = up;
      mesh.userData.down = down;
      mesh.renderOrder = -900;
      group.add(mesh);
      chunks.push(mesh);
    }
    count = B.n + BQ.n;
    built = true;
  };
  const buildNow = () => {
    const t0 = performance.now();
    const data = galaxyData({ isMobile, barAngle });
    genMs = performance.now() - t0;
    build(data);
  };
  const clear = () => {
    for (const c of chunks) { group.remove(c); c.userData.up.dispose(); c.userData.down.dispose(); }
    chunks.length = 0;
  };
  // The first build runs in a worker (galaxyParticles.worker.js): made here
  // it held the main thread for 0.4 to 0.5 s at load, where it was most of
  // what a tap then waited for, and the galaxy is not seen until the camera
  // leaves the solar system. Drawn before the worker has answered, or where
  // a worker cannot run, it is made here as it always was. So it is under
  // the video renderer (window.__vt), which seeds Math.random: three draws
  // four numbers for every object it makes, and objects made when the
  // worker answers would move the deep field's random galaxies (galaxy.js)
  // from one render of a shot to the next.
  let worker = null;
  const stopWorker = () => {
    if (worker) worker.terminate();
    worker = null;
  };
  try {
    if (typeof window !== 'undefined' && window.__vt) throw new Error('video');
    worker = new Worker(new URL('./galaxyParticles.worker.js', import.meta.url), { type: 'module' });
    worker.onmessage = ({ data }) => {
      stopWorker();
      if (built || !data) return;
      genMs = data.genMs;
      build(data);
    };
    worker.onerror = stopWorker;
    worker.postMessage({ isMobile, barAngle, look: { ...LOOK }, palette: { ...P }, structure: { ...MW_STRUCTURE } });
  } catch {
    worker = null;
  }
  if (!worker) buildNow();
  const _v = new THREE.Vector3();
  const _m = new THREE.Matrix4();
  const _size = new THREE.Vector2();
  const _clear = new THREE.Color();
  const _centre = new THREE.Vector3();
  // The particles are drawn with a copy of the view camera whose far plane
  // always reaches past the galaxy: the viewer lets the camera pull back
  // well beyond the scene camera's far plane (225M units in artistic mode),
  // which used to clip the whole galaxy away. The particle target has no
  // depth buffer, so the range costs no precision, and the composite still
  // sits behind anything nearer in the scene.
  const pCam = new THREE.PerspectiveCamera();
  let below = null;
  let grazing = false;
  const targets = new Map();
  const getTarget = (w, h) => {
    const key = w + 'x' + h;
    let rt = targets.get(key);
    if (!rt) {
      if (targets.size >= 3) { const [k0, r0] = targets.entries().next().value; r0.dispose(); targets.delete(k0); }
      rt = new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, depthBuffer: false, generateMipmaps: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
      rt.texture.colorSpace = THREE.NoColorSpace;
      targets.set(key, rt);
    }
    return rt;
  };
  // The composite: a quad over the whole view at the far plane, so it sits
  // behind anything nearer, drawn in the galaxy's place among the
  // transparent objects.
  const compositeMat = new THREE.ShaderMaterial({
    uniforms: { uTex: { value: null } },
    vertexShader: /* glsl */`
      varying vec2 vUv;
      void main() { vUv = position.xy * 0.5 + 0.5; gl_Position = vec4(position.xy, 0.99999, 1.0); }
    `,
    fragmentShader: /* glsl */`
      uniform sampler2D uTex;
      varying vec2 vUv;
      void main() { gl_FragColor = texture2D(uTex, vUv); }
    `,
    transparent: true,
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
    blendSrcAlpha: THREE.ZeroFactor,
    blendDstAlpha: THREE.OneFactor,
    depthWrite: false,
    depthTest: true,
  });
  const composite = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), compositeMat);
  composite.name = 'milkyWayParticles';
  composite.userData.material = material;
  composite.frustumCulled = false;
  composite.renderOrder = -900;
  composite.onBeforeRender = (renderer2, scene, camera) => {
    if (material.uniforms.uOpacity.value <= 0) return;
    if (!built) buildNow();
    const cur = renderer2.getRenderTarget();
    let fw, fh;
    if (cur) { fw = cur.width; fh = cur.height; } else { renderer2.getDrawingBufferSize(_size); fw = _size.x; fh = _size.y; }
    // Resolution: the CSS resolution, stepped down to 0.75 when the view
    // grazes the disc. Edge-on every particle lands on the same thin band of
    // pixels and the blending there is what costs; the step has hysteresis
    // so it does not flip back and forth.
    holder.matrix.copy(composite.parent ? composite.parent.matrixWorld : composite.matrixWorld);
    group.getWorldPosition(_centre);
    _v.set(holder.matrix.elements[8], holder.matrix.elements[9], holder.matrix.elements[10]).normalize();
    const cosView = Math.abs(_v.dot(_centre.sub(camera.position).normalize()));
    if (grazing && cosView > 0.33) grazing = false;
    else if (!grazing && cosView < 0.25) grazing = true;
    const k = Math.min(1, 1 / Math.max(1, renderer2.getPixelRatio())) * (grazing ? 0.75 : 1);
    const w = Math.max(16, Math.round(fw * k)), h = Math.max(16, Math.round(fh * k));
    const rt = getTarget(w, h);
    holder.matrix.copy(composite.parent ? composite.parent.matrixWorld : composite.matrixWorld);
    holder.matrixWorld.copy(holder.matrix);
    holder.updateMatrixWorld(true);
    // Which way the columns are read, and the pixel scale of this target.
    _m.copy(group.matrixWorld).invert();
    _v.copy(camera.position).applyMatrix4(_m);
    const isBelow = _v.z < 0;
    if (isBelow !== below) {
      below = isBelow;
      for (const c of chunks) c.geometry = isBelow ? c.userData.down : c.userData.up;
    }
    material.uniforms.uPxPerRad.value = camera.projectionMatrix.elements[5] * h * 0.5;
    material.uniforms.uViewport.value.set(w, h);
    material.uniforms.uPR.value = 1;
    let drawCam = camera;
    if (camera.isPerspectiveCamera) {
      pCam.copy(camera, false);
      group.getWorldPosition(_centre);
      const reach = camera.position.distanceTo(_centre) + 24 * unitsPerKpc;
      if (reach > pCam.far) { pCam.far = reach; pCam.updateProjectionMatrix(); }
      pCam.matrixWorld.copy(camera.matrixWorld);
      pCam.matrixWorldInverse.copy(camera.matrixWorldInverse);
      pCam.matrixAutoUpdate = false;
      drawCam = pCam;
    }
    const prevAuto = renderer2.autoClear;
    const prevAlpha = renderer2.getClearAlpha();
    renderer2.getClearColor(_clear);
    renderer2.setRenderTarget(rt);
    renderer2.setClearColor(0x000000, 0);
    renderer2.autoClear = true;
    renderer2.render(pScene, drawCam);
    renderer2.setRenderTarget(cur);
    renderer2.setClearColor(_clear, prevAlpha);
    renderer2.autoClear = prevAuto;
    compositeMat.uniforms.uTex.value = rt.texture;
  };
  return {
    group,
    composite,
    material,
    chunks,
    get count() { return count; },
    get genMs() { return genMs; },
    // The live part of GALAXY_LOOK, each frame.
    syncLook() {
      uniforms.uGainStar.value = LOOK.starGain;
      uniforms.uGainEmit.value = LOOK.cloudGain;
      uniforms.uGainDust.value = LOOK.dustGain;
      uniforms.uDustTint.value.set(...rgbOf(P.dust)).multiplyScalar(LOOK.dustGlow);
    },
    rebuild() {
      stopWorker();
      clear();
      buildNow();
      below = null;
      return genMs;
    },
    // Per frame: which way the columns are read, the pixel scale.
    // The camera in kpc in the pattern frame.
    cameraLocal(camera, root) {
      root.updateMatrixWorld();
      _m.multiplyMatrices(root.matrixWorld, group.matrix).invert();
      return _v.copy(camera.position).applyMatrix4(_m);
    },
    dispose() {
      stopWorker();
      clear();
      material.dispose();
      pointMaterial.dispose();
      compositeMat.dispose();
      composite.geometry.dispose();
      targets.forEach((rt) => rt.dispose());
    },
  };
}
